import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Save, Zap, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/** Admin mailbox SMTP settings — connect e.g. one.com `info@social-factory.be`
 * so the platform can send mail drafts via your real outbound server. */
export default function AdminMailSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [form, setForm] = useState({
        smtp_host: "send.one.com",
        smtp_port: 465,
        smtp_user: "",
        smtp_password: "",
        from_name: "Social Factory",
        cc_email: "",
        drip_enabled: false,
        drip_daily_limit: 25,
        drip_photog_daily_limit: 20,
        drip_tenerife_daily_limit: 20,
        drip_followup_daily_limit: 50,
        drip_hour_utc: 8,
        imap_enabled: false,
        imap_host: "imap.one.com",
        imap_port: 993,
        imap_user: "",
        apify_token: "",
    });
    const [configured, setConfigured] = useState(false);
    const [apifyConfigured, setApifyConfigured] = useState(false);
    const [lastTest, setLastTest] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/mail-settings");
                if (data.configured) {
                    setConfigured(true);
                    setForm((f) => ({
                        ...f,
                        smtp_host: data.smtp_host,
                        smtp_port: data.smtp_port,
                        smtp_user: data.smtp_user,
                        from_name: data.from_name || "Social Factory",
                        cc_email: data.cc_email || "",
                        drip_enabled: !!data.drip_enabled,
                        drip_daily_limit: data.drip_daily_limit ?? 25,
                        drip_photog_daily_limit: data.drip_photog_daily_limit ?? 20,
                        drip_tenerife_daily_limit: data.drip_tenerife_daily_limit ?? 20,
                        drip_followup_daily_limit: data.drip_followup_daily_limit ?? 50,
                        drip_hour_utc: data.drip_hour_utc ?? 8,
                        imap_enabled: !!data.imap_enabled,
                        imap_host: data.imap_host || "imap.one.com",
                        imap_port: data.imap_port ?? 993,
                        imap_user: data.imap_user || "",
                    }));
                    setApifyConfigured(!!data.apify_configured);
                    setLastTest({ ok: data.last_test_ok, at: data.last_test_at });
                }
            } catch (e) {
                toast.error("Kon instellingen niet ophalen");
            } finally { setLoading(false); }
        })();
    }, []);

    const save = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (configured && !form.smtp_password) delete payload.smtp_password;
            if (apifyConfigured && !form.apify_token) delete payload.apify_token;
            await api.put("/admin/mail-settings", payload);
            setConfigured(true);
            if (form.apify_token) setApifyConfigured(true);
            setForm((f) => ({ ...f, smtp_password: "", apify_token: "" }));
            toast.success("Mailbox-instellingen opgeslagen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };

    const testConn = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/admin/mail-settings/test");
            setLastTest({ ok: data.ok, at: new Date().toISOString(), error: data.error });
            if (data.ok) toast.success("SMTP-verbinding gelukt 🎉");
            else toast.error(`Verbinding mislukt: ${data.error || "onbekende fout"}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test mislukt");
        } finally { setTesting(false); }
    };

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="admin-mail-settings-page">
            <div className="max-w-[820px] mx-auto px-6 lg:px-10 py-12">
                <Link to="/admin/mails" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-950 mb-8">
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar Mails
                </Link>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-3 inline-flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5" /> Admin · Mailbox instellingen
                </p>
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4">Verbind je mailbox.</h1>
                <p className="text-sm text-zinc-500 mb-10 max-w-xl">
                    Configureer je SMTP-server (bv. one.com) zodat het platform mails kan versturen vanuit jouw eigen e-mailadres. Het wachtwoord wordt encrypted opgeslagen.
                </p>

                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Laden…</div>
                ) : (
                    <form onSubmit={save} className="space-y-5" data-testid="mail-settings-form">
                        <Field label="SMTP server" hint="Voor one.com: send.one.com">
                            <input
                                type="text"
                                value={form.smtp_host}
                                onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                                required
                                data-testid="mail-smtp-host"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>
                        <Field label="Poort" hint="465 (SSL) of 587 (STARTTLS)">
                            <input
                                type="number"
                                value={form.smtp_port}
                                onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value, 10) || 465 })}
                                required
                                data-testid="mail-smtp-port"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>
                        <Field label="E-mailadres (gebruikersnaam)" hint="Het adres waarmee je inlogt op SMTP — dit wordt automatisch ook het 'Van'-adres dat de ontvanger ziet. Voor cold outreach werkt een persoonlijk adres (bv. cyril@social-factory.be) veel beter dan info@.">
                            <input
                                type="email"
                                value={form.smtp_user}
                                onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                                required
                                data-testid="mail-smtp-user"
                                placeholder="bv. cyril@social-factory.be"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>
                        <Field label="Wachtwoord" hint={configured ? "Laat leeg om huidige bewaarde wachtwoord te behouden. Verandert het SMTP-adres hierboven? → vul hier het wachtwoord van die nieuwe mailbox in." : "Het wachtwoord van je mailbox"}>
                            <input
                                type="password"
                                value={form.smtp_password}
                                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                                required={!configured}
                                placeholder={configured ? "•••••••• (opgeslagen)" : ""}
                                data-testid="mail-smtp-password"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>
                        <Field label="Afzender naam" hint="De naam die de ontvanger ziet — bv. 'Cyril · Social Factory' of 'Tom van Social Factory'. Persoonlijke namen verhogen typisch de open-rate met 15-30% versus een generieke brand-naam.">
                            <input
                                type="text"
                                value={form.from_name}
                                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                                data-testid="mail-from-name"
                                placeholder="bv. Cyril · Social Factory"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>

                        {/* Live preview of what the recipient will see in their inbox */}
                        {(form.smtp_user || form.from_name) && (
                            <div className="rounded-sm border border-[#E5A93D]/30 bg-[#E5A93D]/[0.06] px-4 py-3" data-testid="mail-from-preview">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-[#E5A93D] font-bold mb-1">Preview · zo komt het binnen</div>
                                <div className="text-sm text-zinc-800 font-mono">
                                    <span className="text-zinc-500">Van: </span>
                                    <span className="text-zinc-950">{form.from_name || "Social Factory"}</span>
                                    <span className="text-zinc-500"> &lt;{form.smtp_user || "—"}&gt;</span>
                                </div>
                            </div>
                        )}
                        <Field
                            label="Automatische CC"
                            hint="Dit adres krijgt altijd een kopie van elke verstuurde mail. Meerdere adressen mogelijk — gescheiden door komma. Laat leeg om niets te kopiëren — standaard wordt het SMTP-adres hierboven gebruikt."
                        >
                            <input
                                type="text"
                                value={form.cc_email}
                                onChange={(e) => setForm({ ...form, cc_email: e.target.value })}
                                placeholder="bv. info@social-factory.be"
                                data-testid="mail-cc-email"
                                className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                            />
                        </Field>

                        {/* ----------------------------------------------- */}
                        {/* DRIP CAMPAIGN                                   */}
                        {/* ----------------------------------------------- */}
                        <div className="pt-4 mt-4 border-t border-zinc-200">
                            <h2 className="text-sm font-bold text-zinc-950 mb-1 uppercase tracking-wider">Drip-campagne voor leads</h2>
                            <p className="text-xs text-zinc-500 mb-3">
                                Stuur dagelijks automatisch een vast aantal mails. <strong>Intro&apos;s</strong> en <strong>follow-ups</strong>
                                hebben een onafhankelijk dagbudget, zodat een drukke follow-up dag nooit de verse intro&apos;s blokkeert.
                                Voor sender-reputation aanbevolen: 15-25 intro&apos;s/dag, opschalen na 1 week zonder spam-klachten.
                            </p>
                            <Field label="Drip inschakelen" hint="Als uit, blijven leads in 'Nieuw' staan tot je manueel intro klikt.">
                                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="mail-drip-toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.drip_enabled}
                                        onChange={(e) => setForm({ ...form, drip_enabled: e.target.checked })}
                                        className="h-4 w-4 accent-[#E5A93D]"
                                    />
                                    <span className="text-sm text-zinc-800">{form.drip_enabled ? "Drip is actief" : "Drip is uit"}</span>
                                </label>
                            </Field>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <Field label="Intro's makelaars / dag" hint="Nieuwe makelaars / dag (1-200)">
                                    <input
                                        type="number" min={1} max={200}
                                        value={form.drip_daily_limit}
                                        onChange={(e) => setForm({ ...form, drip_daily_limit: parseInt(e.target.value, 10) || 25 })}
                                        data-testid="mail-drip-limit"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="Intro's Tenerife / dag" hint="Tenerife makelaars / dag (0-200)">
                                    <input
                                        type="number" min={0} max={200}
                                        value={form.drip_tenerife_daily_limit}
                                        onChange={(e) => setForm({ ...form, drip_tenerife_daily_limit: parseInt(e.target.value, 10) || 0 })}
                                        data-testid="mail-drip-tenerife-limit"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="Intro's fotografen / dag" hint="Nieuwe fotografen / dag (0-200)">
                                    <input
                                        type="number" min={0} max={200}
                                        value={form.drip_photog_daily_limit}
                                        onChange={(e) => setForm({ ...form, drip_photog_daily_limit: parseInt(e.target.value, 10) || 0 })}
                                        data-testid="mail-drip-photog-limit"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="Follow-ups per dag" hint="Bumps + break-ups / dag (0-500)">
                                    <input
                                        type="number" min={0} max={500}
                                        value={form.drip_followup_daily_limit}
                                        onChange={(e) => setForm({ ...form, drip_followup_daily_limit: parseInt(e.target.value, 10) || 0 })}
                                        data-testid="mail-drip-followup-limit"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="Uur (UTC)" hint="Starttijd dagbatch — 8 UTC = 10:00 BE">
                                    <input
                                        type="number" min={0} max={23}
                                        value={form.drip_hour_utc}
                                        onChange={(e) => setForm({ ...form, drip_hour_utc: parseInt(e.target.value, 10) || 8 })}
                                        data-testid="mail-drip-hour"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                            </div>
                        </div>

                        {/* ----------------------------------------------- */}
                        {/* IMAP REPLY DETECTION                            */}
                        {/* ----------------------------------------------- */}
                        <div className="pt-4 mt-4 border-t border-zinc-200">
                            <h2 className="text-sm font-bold text-zinc-950 mb-1 uppercase tracking-wider">IMAP · automatische reply-detectie</h2>
                            <p className="text-[12px] text-zinc-500 mb-4">
                                Het systeem checkt elke 10 min je mailbox op antwoorden. Matched een afzender met een verzonden intro?
                                De lead-status springt automatisch op &quot;Geantwoord&quot;. Out-of-office mails worden genegeerd.
                            </p>
                            <Field label="IMAP inschakelen" hint="Gebruik hetzelfde wachtwoord als hierboven (SMTP).">
                                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="mail-imap-toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.imap_enabled}
                                        onChange={(e) => setForm({ ...form, imap_enabled: e.target.checked })}
                                        className="h-4 w-4 accent-[#E5A93D]"
                                    />
                                    <span className="text-sm text-zinc-800">{form.imap_enabled ? "IMAP-polling actief" : "IMAP-polling uit"}</span>
                                </label>
                            </Field>
                            <div className="grid grid-cols-3 gap-4">
                                <Field label="IMAP server" hint="Voor one.com: imap.one.com">
                                    <input
                                        type="text"
                                        value={form.imap_host}
                                        onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                                        data-testid="mail-imap-host"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="Poort" hint="993 = IMAPS (SSL)">
                                    <input
                                        type="number" min={1} max={65535}
                                        value={form.imap_port}
                                        onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })}
                                        data-testid="mail-imap-port"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                                <Field label="IMAP gebruiker" hint="Laat leeg = gebruikt SMTP-adres">
                                    <input
                                        type="email"
                                        value={form.imap_user}
                                        onChange={(e) => setForm({ ...form, imap_user: e.target.value })}
                                        placeholder="(automatisch)"
                                        data-testid="mail-imap-user"
                                        className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                    />
                                </Field>
                            </div>
                        </div>

                        {/* ----------------------------------------------- */}
                        {/* AI PROSPECTING (Apify token)                    */}
                        {/* ----------------------------------------------- */}
                        <div className="pt-4 mt-4 border-t border-zinc-200">
                            <h2 className="text-sm font-bold text-zinc-950 mb-1 uppercase tracking-wider">AI Prospecting · Apify token</h2>
                            <p className="text-[12px] text-zinc-500 mb-4">
                                Voor de &quot;Demo-mails klaarzetten&quot; feature in /admin/leads. Vereist een Apify API-token (
                                <a href="https://console.apify.com/account/integrations" target="_blank" rel="noreferrer" className="text-[#E5A93D] hover:underline">console.apify.com/account/integrations</a>
                                ). Status: {apifyConfigured ? <span className="text-emerald-600 font-bold">geconfigureerd ✓</span> : <span className="text-amber-600 font-bold">niet ingesteld</span>}
                            </p>
                            <Field label="Apify API token" hint={apifyConfigured ? "Laat leeg om huidige te behouden" : "Begint met 'apify_api_…'"}>
                                <input
                                    type="password"
                                    value={form.apify_token}
                                    onChange={(e) => setForm({ ...form, apify_token: e.target.value })}
                                    placeholder={apifyConfigured ? "•••••••• (huidig blijft als leeg)" : "apify_api_…"}
                                    autoComplete="new-password"
                                    data-testid="mail-apify-token"
                                    className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60"
                                />
                            </Field>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="mail-settings-save"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-sm bg-white text-zinc-950 font-bold text-sm hover:bg-zinc-100 transition"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Opslaan
                            </button>
                            <button
                                type="button"
                                onClick={testConn}
                                disabled={testing || !configured}
                                data-testid="mail-settings-test"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-sm border border-[#E5A93D]/40 text-[#E5A93D] font-bold text-sm hover:bg-[#E5A93D]/10 transition disabled:opacity-50"
                            >
                                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                                Test verbinding
                            </button>
                            {lastTest && (
                                <span className={`inline-flex items-center gap-1.5 text-xs ${lastTest.ok ? "text-emerald-600" : "text-rose-600"}`} data-testid="mail-settings-test-status">
                                    {lastTest.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                                    {lastTest.ok ? "Verbinding gelukt" : `Mislukt${lastTest.error ? ": " + lastTest.error : ""}`}
                                </span>
                            )}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">{label}</label>
            {children}
            {hint && <p className="mt-1.5 text-[11px] text-zinc-500">{hint}</p>}
        </div>
    );
}
